import { Clock, FolderKanban, Bot } from "lucide-react";
import type { MentionItem } from "@/types/mention";
import { PROJECT_ICON_MAP, AGENT_CONFIG } from "@/lib/constants";
import { getProjectColorClasses } from "@/lib/utils/formatting";

interface MentionMenuItemProps {
  item: MentionItem;
  isSelected: boolean;
  onClick: () => void;
  onHover: () => void;
}

const MentionMenuItem = ({
  item,
  isSelected,
  onClick,
  onHover,
}: MentionMenuItemProps) => {
  const getIcon = () => {
    if (item.type === "time") {
      return <Clock className="w-4 h-4 text-muted-foreground" />;
    }

    if (item.type === "agent") {
      const agentConfig = AGENT_CONFIG[item.value as keyof typeof AGENT_CONFIG];
      if (agentConfig?.icon) {
        return (
          <img
            src={agentConfig.icon}
            alt={item.label}
            className="w-5 h-5 rounded flex-shrink-0"
          />
        );
      }
      return <Bot className="w-4 h-4 text-muted-foreground" />;
    }

    // For branches and projects, use the project icon with proper styling
    if (item.type === "branch" || item.type === "project") {
      const IconComponent =
        item.icon &&
        PROJECT_ICON_MAP[item.icon as keyof typeof PROJECT_ICON_MAP]
          ? PROJECT_ICON_MAP[item.icon as keyof typeof PROJECT_ICON_MAP]
          : FolderKanban;

      const projectColors = getProjectColorClasses(item.color || "gray");

      return (
        <div
          className={`w-5 h-5 rounded ${projectColors.bg} ${projectColors.border} border flex items-center justify-center flex-shrink-0`}
        >
          <IconComponent className={`w-3 h-3 ${projectColors.text}`} />
        </div>
      );
    }

    return null;
  };

  return (
    <button
      onClick={onClick}
      onMouseEnter={onHover}
      className={`
        w-full flex items-center gap-3 px-3 py-2 rounded-md text-left
        transition-colors duration-150
        ${
          isSelected
            ? "bg-accent text-foreground"
            : "text-foreground hover:bg-muted"
        }
      `}
    >
      {getIcon()}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{item.label}</div>
        {item.type === "branch" && item.projectName && (
          <div className="text-xs text-muted-foreground">in {item.projectName}</div>
        )}
      </div>
      {item.type === "project" && (
        <span className="text-xs text-muted-foreground">project</span>
      )}
      {item.type === "branch" && (
        <span className="text-xs text-muted-foreground">branch</span>
      )}
      {item.type === "agent" && (
        <span className="text-xs text-muted-foreground">agent</span>
      )}
      {item.type === "time" && (
        <span className="text-xs text-muted-foreground">time</span>
      )}
    </button>
  );
};

export default MentionMenuItem;
